import isEmpty from "lodash/isEmpty";

import { submitHubspotFormDataAPI } from "@stadium/services/hubspot/api";

import { jsonDataFormatter } from "@stadium/helpers/formatter";
import { getCookieByName, getClientIpFromGeoCookie } from "@stadium/helpers/cookie";

import { HUBSPOT_UTK_COOKIE_NAME } from "@stadium/services/hubspot/constants";

export const HUBSPOT_CONTACT_OBJECT_TYPE_ID = "0-1";

/**
 * Returns the hubspotutk cookie value for HubSpot `context.hutk` when allowed to send.
 * When Osano CMP is on the page, the HubSpot cookie is only forwarded after marketing consent
 * (`globalThis.Osano.cm.marketing === true` per Osano docs). If the user declines marketing, hutk is omitted
 * even if a stale cookie string were readable.
 */
const getHubspotUtkForFormSubmission = () => {
  const raw = getCookieByName(HUBSPOT_UTK_COOKIE_NAME);
  if (isEmpty(raw)) return undefined;

  if (globalThis.Osano?.cm) {
    return globalThis.Osano.cm.marketing === true ? raw : undefined;
  }
  return raw;
};

/**
 * Builds the HubSpot Forms API v3 body: top-level `submittedAt`, `fields`, and `context`
 * (pageUri, pageName, hutk when available, ipAddress when available).
 * `context.ipAddress` uses the explicit `ipAddress` argument when passed; otherwise
 * {@link getClientIpFromGeoCookie} (geo cookie `ip_address` from site middleware).
 * Client-only: call from browser handlers or hooks after mount (not during SSR).
 */
export const buildHubspotFormSubmissionBody = ({ fields, pageName = "" } = {}) => {
  const context = {
    pageUri: globalThis.location.href,
    ...(pageName && { pageName }),
  };
  const hutk = getHubspotUtkForFormSubmission();
  if (!isEmpty(hutk)) {
    context.hutk = hutk;
  }
  const resolvedIp = getClientIpFromGeoCookie();
  if (!isEmpty(resolvedIp)) {
    context.ipAddress = resolvedIp;
  }
  return {
    submittedAt: Date.now(),
    fields,
    context,
  };
};

export const submitFormData = (formGuid, params) =>
  submitHubspotFormDataAPI(formGuid, params).then((response) => {
    const data = jsonDataFormatter(response);
    return data;
  });

const buildHubspotMappedFields = (formHubspotMapping, params) => {
  const fields = [];
  Object.keys(params)
    .filter((key) => formHubspotMapping[key])
    .forEach((key) => {
      fields.push({
        objectTypeId: HUBSPOT_CONTACT_OBJECT_TYPE_ID,
        name: formHubspotMapping[key],
        value: params[key],
      });
    });
  return fields;
};

const parseParamsForHubspotLegacy = (formHubspotMapping, params, pageName) => {
  const parsedFormValuesForHubspot = {
    context: {
      pageUri: globalThis.location.href,
      pageName,
    },
  };
  const fields = buildHubspotMappedFields(formHubspotMapping, params);
  const hubspotutk = getCookieByName(HUBSPOT_UTK_COOKIE_NAME);
  if (!isEmpty(hubspotutk)) {
    parsedFormValuesForHubspot.context.hutk = hubspotutk;
  }
  parsedFormValuesForHubspot.fields = fields;
  return parsedFormValuesForHubspot;
};

/**
 * @param {boolean} [isHubspotContextRefactor] When true, uses Osano-aware hutk, geo IP in context, and `submittedAt`.
 */
export const parseParamsForHubspot = (
  formHubspotMapping,
  params,
  pageName,
  isHubspotContextRefactor = false,
) => {
  if (!isHubspotContextRefactor) {
    return parseParamsForHubspotLegacy(formHubspotMapping, params, pageName);
  }
  const fields = buildHubspotMappedFields(formHubspotMapping, params);
  return buildHubspotFormSubmissionBody({ fields, pageName });
};
